import { markInputRule, markPasteRule } from '@tiptap/core';
import Italic from '@tiptap/extension-italic';
import { MarkdownMarkSpec } from 'tiptap-markdown';
import { preventSpacelessPunctuation, preventStartingWithPunctuation } from './Bold';
import { ItalicToken } from '@scale/shared/types/config';

/**
 * Matches an italic to a *italic* on input.
 */
export const starInputRegex = /(?:^|\s)(\*(?!\s+\*)((?:[^*]+))\*(?!\s+\*))$/;

/**
 * Matches an italic to a *italic* on paste.
 */
export const starPasteRegex = /(?:^|\s)(\*(?!\s+\*)((?:[^*]+))\*(?!\s+\*))/g;

/**
 * Matches an italic to a _italic_ on input.
 */
export const underscoreInputRegex = /(?:^|\s)(_(?!\s+_)((?:[^_]+))_(?!\s+_))$/;

/**
 * Matches an italic to a _italic_ on paste.
 */
export const underscorePasteRegex = /(?:^|\s)(_(?!\s+_)((?:[^_]+))_(?!\s+_))/g;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    italic_extended: {
      toggleItalicWithDefaultToken: () => ReturnType;
    };
  }
}

function getToken(tokenEnum: ItalicToken) {
  return tokenEnum;
}

export default Italic.extend({
  addStorage(): { markdown: MarkdownMarkSpec } {
    return {
      markdown: {
        // Source: https://github.com/ProseMirror/prosemirror-markdown/blob/master/src/to_markdown.ts#L133serialize
        serialize: {
          open: (_state, mark) => {
            return mark.attrs.token;
          },
          close: (_state, mark) => {
            return mark.attrs.token;
          },
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(markdownit) {
            markdownit.renderer.rules.em_open = (tokens, idx, options, _env, self) => {
              // Get the token being processed
              const token = tokens[idx];

              // Check the delimiter type used (token has '_' or '*')
              const markup = token.markup; // `*` or `_`

              // Add a custom attribute based on the markup used
              token.attrSet('token', markup);

              // Pass token to default renderer with the custom attribute
              return self.renderToken(tokens, idx, options);
            };
          },
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      toggleItalicWithDefaultToken:
        () =>
        ({ commands }) => {
          const defaultItalicToken =
            this.editor.storage.markdownExtensions.italicToken ?? ItalicToken.ASTERISK;
          commands.toggleMark(this.name, { token: getToken(defaultItalicToken) });
          return true;
        },
    };
  },
  addAttributes() {
    return {
      token: {
        default: '*',
        renderHTML: attributes => {
          return {
            token: attributes.token,
          };
        },
      },
    };
  },

  addInputRules() {
    return [
      markInputRule({
        find: starInputRegex,
        type: this.type,
        getAttributes() {
          return { token: '*' };
        },
      }),
      markInputRule({
        find: underscoreInputRegex,
        type: this.type,
        getAttributes() {
          return { token: '_' };
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: starPasteRegex,
        type: this.type,
        getAttributes() {
          return { token: '*' };
        },
      }),
      markPasteRule({
        find: underscorePasteRegex,
        type: this.type,
        getAttributes() {
          return { token: '_' };
        },
      }),
    ];
  },
  addProseMirrorPlugins() {
    return [preventSpacelessPunctuation('italic'), preventStartingWithPunctuation('italic')];
  },
});
